import React, { useState, useEffect } from 'react'

// Hooks
import useDataLayer from '../../../hooks/useDataLayer/dataLayerBody'

import { formatArea } from '../../../utils/format'
import { themes } from '../../../styles/theme'

import IcChevronRight from '@interco/icons/core/action-navigation/ic_chevron_right'
import { gray, grayscale } from '../../../styles/colors'

// shared
import removeAcento from '../../../shared/removeAcentos'

// Hooks
import useWidth from '../../../hooks/window/useWidth'

// Components
import LoadingIndicatorComponent from '../../../components/LoadingIndicator/index'
import { Tag } from '../../../components/Tag/index'
import NotFoundModal from './NotFound/NotFoundModal/_index'
import NotFoundMessage from './NotFound/NotFoundMessage/_index'

import { pageProps, apiData, categoriesProps } from './types'

import * as S from './style'

const WIDTH_MD = 768

const hasValidMetadata = (item: apiData): boolean => Array.isArray(item.metadata)

const isAreaMeta = (meta: categoriesProps): boolean =>
  Boolean(meta.name && removeAcento(meta.name).toLowerCase() === 'area')

const getAreaCategory = (item: apiData): categoriesProps | undefined =>
  hasValidMetadata(item) ? item.metadata.find(isAreaMeta) : undefined

const getAreaValue = (item: apiData): string | undefined => {
  const category = getAreaCategory(item)
  return category?.value?.[0] ?? undefined
}

const matchesSearchText = (item: apiData, lowerSearchText: string): boolean => {
  if (item.title?.toLowerCase().includes(lowerSearchText)) return true
  if (item.location?.name?.toLowerCase().includes(lowerSearchText)) return true

  if (!hasValidMetadata(item)) return false
  const metaValues = item.metadata[1]?.value
  return Array.isArray(metaValues) && metaValues.some(
    (value: string) => typeof value === 'string' && value.toLowerCase().includes(lowerSearchText),
  )
}

const JobList = ({ searchText, jobsLocation, area, JobsContent, selectedAreas, selectedCargos, handleClose, modal }: pageProps) => {
  const [ categories, setCategories ] = useState<string[]>([])
  const [ sendDatalayerEvent ] = useDataLayer()
  const windowWidth = useWidth(100)

  const notFound = windowWidth >= WIDTH_MD ? <NotFoundMessage /> : <NotFoundModal modal={modal} handleClose={handleClose} />

  useEffect(() => {
    if (JobsContent.length > 0) {
      const areaNames = JobsContent
        .map(getAreaValue)
        .filter((value): value is string => value != null)

      const uniqueAreas = Array.from(new Set(areaNames))
      setCategories(uniqueAreas)
    }
  }, [ searchText, jobsLocation, area, JobsContent ])

  if (JobsContent?.length > 0) {
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase()
      JobsContent = JobsContent.filter((item: apiData) => matchesSearchText(item, lowerSearchText))
    }

    if (jobsLocation) {
      JobsContent = JobsContent.filter((item: apiData) =>
        item.location?.name && jobsLocation.includes(item.location.name.toLowerCase()),
      )
    }

    if (area) {
      JobsContent = JobsContent.filter((item: apiData) => getAreaValue(item) === area)
    }

    if (selectedAreas.length > 0) {
      JobsContent = JobsContent.filter((item: apiData) => {
        const areaValue = getAreaValue(item)
        return areaValue != null && selectedAreas.includes(areaValue)
      })
    }

    if (selectedCargos.length > 0) {
      JobsContent = JobsContent.filter((item: apiData) => selectedCargos.includes(item.title))
    }

    if (JobsContent?.length === 0) {
      return notFound
    }
  }

  const getJobColor = (area: string) => {
    const formattedArea = formatArea(area).toLowerCase()
    return themes[formattedArea as keyof typeof themes]?.variant4.background || gray[400]
  }

  return (
    <>
      {(!JobsContent || JobsContent.length < 1) ? <div className='d-flex justify-content-center'><LoadingIndicatorComponent /></div>
        : <S.JobContent className='mb-5'>
          <div className='row d-flex justify-content-between'>
            <div className='col-12'>
              {
                JobsContent.map((obj: apiData) => {
                  const areaCategory = getAreaCategory(obj)
                  const areaName = areaCategory?.value?.[0]
                  const displayTitle = (obj.title.includes('DEV IOS') && obj.title.replace('DEV IOS', 'Dev IOS'))
                    || obj.title.toLowerCase().replace('iii', 'III').replace('ii', 'II')

                  return (
                    <a
                      onClick={() => {
                        sendDatalayerEvent({
                          section: 'dobra_2',
                          element_action: 'click button',
                          element_name: displayTitle,
                          section_name: '',
                          redirect_url: obj.absolute_url,
                        })
                      }}
                      className='link'
                      href={obj.absolute_url}
                      title={obj.title}
                      key={obj.id}
                    >
                      <S.CardJob>
                        <div className='d-flex align-items-center mb-3'>
                          {areaName && (
                            <Tag size='small' color={getJobColor(areaName)} colorText={grayscale[500]}>
                              {areaName}
                            </Tag>
                          )}
                        </div>
                        <div className='d-flex justify-content-between'>
                          <h4 className='fs-18 lh-20 text-grascale--500 fw-600 title mb-0'>
                            {displayTitle}
                          </h4>
                          <div>
                            <IcChevronRight height={24} width={24} color='#FF7A00' />
                          </div>
                        </div>
                      </S.CardJob>
                    </a>
                  )
                })
              }
            </div>
          </div>
        </S.JobContent>
      }
    </>
  )
}

export default JobList
